import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { ArrowLeft, Calendar, User, Folder } from 'lucide-react';
import axios from 'axios';
import { updateMetaTags, setCanonicalUrl, addStructuredData, generateArticleStructuredData } from '../utils/seoHelper';
import { extractHeadings, generateTableOfContents } from '../utils/htmlParser';
import TableOfContents from '../components/blog/TableOfContents';
import ImageZoomModal from '../components/blog/ImageZoomModal';

interface BlogPost {
  slug: string;
  title: string;
  description: string;
  content: string;
  author: string;
  category: string;
  tags: string[];
  image: string;
  published: boolean;
  created_at: string;
  id: string;
}

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [nextPost, setNextPost] = useState<BlogPost | null>(null);
  const [prevPost, setPrevPost] = useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [headings, setHeadings] = useState<any[]>([]);
  const [zoomModal, setZoomModal] = useState<{ isOpen: boolean; imageUrl: string | null; altText: string }>({
    isOpen: false,
    imageUrl: null,
    altText: '',
  });

  // SEO - Update meta tags and structured data
  useEffect(() => {
    if (post) {
      const url = `${window.location.origin}/blog/${post.slug}`;
      
      // Update meta tags
      updateMetaTags({
        title: `${post.title} | Metapic Blog`,
        description: post.description || `Read about ${post.title} on Metapic blog`,
        image: post.image,
        url: url,
        type: 'article',
        author: post.author,
        published: post.created_at,
        modified: post.updated_at || post.created_at,
      });
      
      // Set canonical URL
      setCanonicalUrl(url);
      
      // Add structured data
      const cleanup = addStructuredData(
        generateArticleStructuredData({
          headline: post.title,
          description: post.description,
          image: post.image || 'https://metapic.ai/og-image.png',
          datePublished: post.created_at,
          dateModified: post.updated_at || post.created_at,
          author: post.author,
          url: url,
        })
      );
      
      return cleanup;
    }
  }, [post]);

  useEffect(() => {
    const loadPost = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'https://api.metapic.app';
        
        // Fetch single post
        const postResponse = await axios.get(`${apiUrl}/api/v1/blog/${slug}`);
        
        // Handle response format: {success: true, data: {data: {...}}}
        let postData = null;
        if (postResponse.data.success && postResponse.data.data?.data) {
          postData = postResponse.data.data.data;
        } else if (postResponse.data.success && postResponse.data.data) {
          postData = postResponse.data.data;
        } else if (postResponse.data.data) {
          postData = postResponse.data.data;
        }
        
        if (!postData) {
          setError('Post not found');
          setIsLoading(false);
          return;
        }
        
        setPost(postData);
        
        // Extract headings from content for table of contents
        if (postData.content) {
          const extracted = extractHeadings(postData.content);
          const toc = generateTableOfContents(extracted);
          setHeadings(toc);
        }

        // Fetch all posts untuk get next/prev dan related
        const allResponse = await axios.get(`${apiUrl}/api/v1/blog?limit=100`);
        const allPosts = allResponse.data.success 
          ? allResponse.data.data.data 
          : Array.isArray(allResponse.data) 
            ? allResponse.data 
            : allResponse.data.data || [];

        // Find current post index (sorted by created_at DESC)
        const sortedPosts = [...allPosts].sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        
        const currentIndex = sortedPosts.findIndex((p: BlogPost) => p.slug === slug);

        // Get next/prev posts (next = older, prev = newer)
        if (currentIndex > 0) {
          setPrevPost(sortedPosts[currentIndex - 1]); // Newer post
        }
        if (currentIndex < sortedPosts.length - 1) {
          setNextPost(sortedPosts[currentIndex + 1]); // Older post
        }

        // Get related posts (same category, max 3, exclude current)
        const related = allPosts
          .filter((p: BlogPost) => p.category === postData.category && p.slug !== slug)
          .slice(0, 3);
        setRelatedPosts(related);

      } catch (err) {
        console.error('Error loading post:', err);
        setError('Failed to load post');
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) {
      loadPost();
    }
  }, [slug]);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Unknown date';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Unknown date';
    }
  };

  const readingTime = (content: string | undefined) => {
    if (!content) return 0;
    return Math.ceil(content.split(' ').length / 200);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading article...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !post) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-6"
          >
            <ArrowLeft size={20} /> Back to blog
          </button>
          <div className="bg-red-50 text-red-800 p-4 rounded-lg">
            {error || 'Post not found'}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-6 mb-6">
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center gap-2 text-white mb-3 hover:opacity-80 text-sm"
          >
            <ArrowLeft size={16} /> Back to blog
          </button>
          <h1 className="text-3xl font-bold text-white mb-3">{post.title}</h1>
          <div className="flex flex-wrap gap-4 text-blue-50 text-sm">
            <div className="flex items-center gap-1">
              <Calendar size={14} />
              {formatDate(post.created_at)}
            </div>
            <div className="flex items-center gap-1">
              <User size={14} />
              {post.author}
            </div>
            <div className="flex items-center gap-1">
              <Folder size={14} />
              {post.category}
            </div>
            <div className="text-blue-50">
              ~{readingTime(post.content)} min read
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Table of Contents */}
        {headings && headings.length > 0 && (
          <div className="mb-8">
            <TableOfContents headings={headings} />
          </div>
        )}

        {/* Post Content */}
        <div className="max-w-none mb-8">
          <div
            ref={(el) => {
              // Add click handlers to images when content is rendered
              if (el) {
                setTimeout(() => {
                  // Add IDs to headings for Table of Contents linking
                  const headingElements = el.querySelectorAll('h1, h2, h3, h4, h5, h6');
                  headingElements.forEach((heading: Element, index: number) => {
                    const level = parseInt(heading.tagName[1]);
                    const text = heading.textContent || '';
                    const slug = text
                      .toLowerCase()
                      .trim()
                      .replace(/[^\w\s-]/g, '')
                      .replace(/\s+/g, '-')
                      .replace(/-+/g, '-');
                    
                    // Set ID for linking from Table of Contents
                    if (!heading.id) {
                      heading.id = slug || `heading-${level}-${index}`;
                    }
                  });
                  
                  const images = el.querySelectorAll('img');
                  images.forEach((img: HTMLImageElement) => {
                    img.setAttribute('loading', 'lazy');
                    img.setAttribute('decoding', 'async');
                    img.style.cursor = 'pointer';
                    img.style.transition = 'opacity 0.2s ease';
                    
                    // Remove old listeners to avoid duplicates
                    const newImg = img.cloneNode(true) as HTMLImageElement;
                    img.parentNode?.replaceChild(newImg, img);
                    
                    // Add new listener
                    newImg.addEventListener('mouseenter', () => {
                      newImg.style.opacity = '0.8';
                    });
                    newImg.addEventListener('mouseleave', () => {
                      newImg.style.opacity = '1';
                    });
                    newImg.addEventListener('click', (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const altText = newImg.getAttribute('alt') || 'Image';
                      const src = newImg.getAttribute('src');
                      if (src) {
                        setZoomModal({
                          isOpen: true,
                          imageUrl: src,
                          altText,
                        });
                      }
                    });
                  });
                  
                  // Apply dark mode text color to all paragraphs
                  const paragraphs = el.querySelectorAll('p');
                  paragraphs.forEach((p: HTMLParagraphElement) => {
                    p.style.color = '#111827'; // gray-900
                    p.style.marginBottom = '0.75rem';
                    p.style.lineHeight = '1.75';
                  });
                  
                  // Apply styles to all child elements
                  const h1s = el.querySelectorAll('h1');
                  h1s.forEach((h: HTMLElement) => {
                    h.style.fontSize = '1.875rem';
                    h.style.fontWeight = 'bold';
                    h.style.marginTop = '1.5rem';
                    h.style.marginBottom = '0.75rem';
                    h.style.color = '#111827';
                  });
                  
                  const h2s = el.querySelectorAll('h2');
                  h2s.forEach((h: HTMLElement) => {
                    h.style.fontSize = '1.5rem';
                    h.style.fontWeight = 'bold';
                    h.style.marginTop = '1.25rem';
                    h.style.marginBottom = '0.5rem';
                    h.style.color = '#111827';
                  });
                  
                  const h3s = el.querySelectorAll('h3');
                  h3s.forEach((h: HTMLElement) => {
                    h.style.fontSize = '1.25rem';
                    h.style.fontWeight = 'bold';
                    h.style.marginTop = '1rem';
                    h.style.marginBottom = '0.5rem';
                    h.style.color = '#111827';
                  });
                  
                  // Apply styles to unordered lists (bullets)
                  const uls = el.querySelectorAll('ul');
                  uls.forEach((ul: HTMLElement) => {
                    ul.style.listStyleType = 'disc';
                    ul.style.paddingLeft = '1.5rem';
                    ul.style.marginBottom = '1rem';
                    ul.style.color = '#111827';
                  });
                  
                  // Apply styles to ordered lists (numbers)
                  const ols = el.querySelectorAll('ol');
                  ols.forEach((ol: HTMLElement) => {
                    ol.style.listStyleType = 'decimal';
                    ol.style.paddingLeft = '1.5rem';
                    ol.style.marginBottom = '1rem';
                    ol.style.color = '#111827';
                  });
                  
                  // Apply styles to list items
                  const lis = el.querySelectorAll('li');
                  lis.forEach((li: HTMLElement) => {
                    li.style.marginBottom = '0.5rem';
                    li.style.lineHeight = '1.75';
                    li.style.color = '#111827';
                  });
                  
                  // Apply styles to links
                  const links = el.querySelectorAll('a');
                  links.forEach((link: HTMLAnchorElement) => {
                    link.style.color = '#3b82f6'; // Blue color
                    link.style.textDecoration = 'underline';
                    link.style.transition = 'color 0.2s ease';
                    link.style.wordBreak = 'break-all'; // Break long URLs
                    link.style.overflowWrap = 'break-word';
                    
                    // Hover effect
                    link.addEventListener('mouseenter', () => {
                      link.style.color = '#2563eb'; // Darker blue
                    });
                    link.addEventListener('mouseleave', () => {
                      link.style.color = '#3b82f6'; // Back to blue
                    });
                  });
                }, 50);
              }
            }}
            dangerouslySetInnerHTML={{ __html: post.content }}
            className="space-y-4 leading-relaxed text-gray-900 dark:text-gray-100 overflow-x-hidden"
            style={{ color: '#111827', wordBreak: 'break-word', overflowWrap: 'break-word' }}
          />
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mb-6 pt-4 border-t">
            <p className="text-gray-600 mb-2 font-semibold text-sm">Tags:</p>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Next/Prev Navigation */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-6 border-t">
          {prevPost ? (
            <button
              onClick={() => navigate(`/blog/${prevPost.slug}`)}
              className="text-left p-3 hover:bg-gray-50 rounded-lg transition group"
            >
              <p className="text-xs text-gray-500 mb-1 group-hover:text-gray-700">← Previous Post</p>
              <p className="font-semibold text-sm text-gray-900 group-hover:text-blue-600 line-clamp-2">{prevPost.title}</p>
              <p className="text-xs text-gray-500 mt-1">{formatDate(prevPost.created_at)}</p>
            </button>
          ) : (
            <div />
          )}
          {nextPost ? (
            <button
              onClick={() => navigate(`/blog/${nextPost.slug}`)}
              className="text-right p-3 hover:bg-gray-50 rounded-lg transition group"
            >
              <p className="text-xs text-gray-500 mb-1 group-hover:text-gray-700">Next Post →</p>
              <p className="font-semibold text-sm text-gray-900 group-hover:text-blue-600 line-clamp-2">{nextPost.title}</p>
              <p className="text-xs text-gray-500 mt-1">{formatDate(nextPost.created_at)}</p>
            </button>
          ) : (
            <div />
          )}
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="py-6">
            <h2 className="text-xl font-bold mb-4">Related Posts</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedPosts.map((relPost) => (
                <button
                  key={relPost.slug}
                  onClick={() => navigate(`/blog/${relPost.slug}`)}
                  className="text-left hover:opacity-80 transition rounded-lg overflow-hidden hover:shadow-lg"
                >
                  {relPost.image && (
                    <img
                      src={relPost.image}
                      alt={relPost.title}
                      className="w-full h-32 object-cover"
                    />
                  )}
                  <div className="p-3">
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{relPost.category}</span>
                    <h3 className="font-semibold text-gray-900 mt-1 line-clamp-2 text-sm">{relPost.title}</h3>
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">{relPost.description}</p>
                    <p className="text-xs text-gray-500 mt-2">{formatDate(relPost.created_at)}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Image Zoom Modal */}
      <ImageZoomModal
        isOpen={zoomModal.isOpen}
        imageUrl={zoomModal.imageUrl}
        altText={zoomModal.altText}
        onClose={() => setZoomModal({ isOpen: false, imageUrl: null, altText: '' })}
      />
    </Layout>
  );
};

export default BlogPostPage;
